import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MSkeleton } from '@Components/Base';
import { PresentationContainer, ViewResolver } from '@Components/index';
import { RequestListSearchResponse } from '@Types/api/requestList';
import { FormikHelpers, useFormik } from 'formik';

import {
  useAppDispatch,
  useAppSelector,
  useIsLoadedConent,
  useRequestRefetcher,
  useStatusFinderByNum,
  useUserPermissions,
} from '@Hooks/index';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';

import { FiltersWidget } from '@Components/FiltersWidget';
import { ViewWidget } from '@Components/ViewWidget';
import { NotificationContext } from '@Context/NotificationContext';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import PurchaseRequestList from '@Features/MaterialRequest/requests/components/PurchaseRequestList/PurchaseRequestList';
import { SortConfig } from '@Features/types/SortConfig';
import { parseSelectedFiltersToOptions } from '@Features/utils/parseSelectedFiltersToOptions';
import { prepareSearchCriterias } from '@Features/utils/prepareSearchCriterias';
import {
  getPurchaseClassifications,
  getPurchaseStatusesDictSelector,
} from '@Store/selectors/dictionarySelector';
import { isFiltersEmptySelector } from '@Store/selectors/filterOptionsSelector';
import { getModalStatus } from '@Store/selectors/modalSelector';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { modalActions } from '@Store/slices/modalSlice';
import { SearchCriteria } from '@Types/api/common/filters';
import { PurchaseFormResponse } from '@Types/api/form';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import { manageRequestActions } from '@Features/store/ManageRequestSlice';
import {
  useDeletePurchaseRequestMutation,
  useExportPurchaseRequestMutation,
  useImportPurchaseRequestMutation,
} from './store/api/purchaseRequestApiSlice';
import RequestViewMobileForm from './form/View/mobile/RequestViewMobileForm';
import RequestViewForm from './form/View/RequestViewForm';
import { dataSourceMap } from './list/DataSource';
import { mapColumns } from './list/MapColumns';
import Filters from './search/Filter';
import {
  filterTypes,
  getFiltersMapper,
  initialValuesFilterForm,
} from './search/filters.utils';
import { FilterForm } from './search/types';
import { PaymentButtonGroup } from '../components/PaymentButtonGroup';

type handleApplyFilterType = (
  values: FilterForm,
  formikHelpers: FormikHelpers<FilterForm>
) => void;

const PurchaseRequest: React.FC = () => {
  const { deleteSuccessNotification, successNotification, errorNotification } =
    useContext(NotificationContext);
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();
  const isRequestFormPagePrev = useRef(isRequestFormPage);

  const isFiltersEmpty = useAppSelector(isFiltersEmptySelector);
  const isModalOpen = useAppSelector(getModalStatus);

  const statuses = useAppSelector(getPurchaseStatusesDictSelector);
  const purchaseClassifications = useAppSelector(getPurchaseClassifications);

  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [deletePurchaseRequest] = useDeletePurchaseRequestMutation();
  const [importPurchaseRequest] = useImportPurchaseRequestMutation();
  const [exportPurchaseRequest] = useExportPurchaseRequestMutation();
  const [tagsValues, setTagsValues] = useState(initialValuesFilterForm);
  const [showTags, setShowTags] = useState(false);
  const [showFiltersError, setShowFiltersError] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [sorting, setSorting] = useState<SortConfig | null>(null);
  const [filters, setFilters] = useState<SearchCriteria[] | undefined>(
    undefined
  );
  const permissions = useUserPermissions();

  const closeFilters = (): void => {
    setIsFiltersOpen(false);
  };

  const openFilters = (): void => {
    setIsFiltersOpen(true);
  };

  const handleApplyFilters: handleApplyFilterType = (options): void => {
    const filtersMapper = getFiltersMapper(statuses, purchaseClassifications);
    const searchCriterias = prepareSearchCriterias(
      options,
      filterTypes,
      filtersMapper
    );
    setFilters(searchCriterias);

    const parsedOptions = parseSelectedFiltersToOptions(options);

    // true if filters was empty and empty after submit
    const isFiltersStillEmpty = isFiltersEmpty && !parsedOptions.length;
    setShowFiltersError(isFiltersStillEmpty);

    if (!isFiltersStillEmpty) {
      closeFilters();
    }

    dispatch(filterOptionsActions.setFilterSettings(searchCriterias));
    dispatch(filterOptionsActions.setFilterOptions(parsedOptions));
    dispatch(eventMessageActions.sendEventType('FILTER'));

    setShowTags(true);
    setTagsValues(options);
  };

  const formik = useFormik({
    initialValues: initialValuesFilterForm,
    enableReinitialize: true,
    onSubmit: handleApplyFilters,
  });

  const clearParams = (): void => {
    setSorting(null);
    setFilters(undefined);
    setSearchValue(undefined);
    setTagsValues(initialValuesFilterForm);
    formik.setValues(initialValuesFilterForm);
  };

  const {
    currentPage,
    fetchRequest,
    handleLoadMore,
    onChangeCurrentPage,
    isSuccess,
    listRequests,
    requestsDataList,
  } = useRequestRefetcher({
    clearParams,
    filters,
    isRequestFormPage,
    isRequestFormPagePrev,
    requestType: 'Purchase',
    searchValue,
    sorting,
  });
  const isContentLoaded = useIsLoadedConent(isSuccess);

  const { findStatus } = useStatusFinderByNum('Purchase');

  const handleCreateTender: () => void = () => {
    navigate(`${pathname}/create`);
  };

  const handleImport = (recordKey: string): void => {
    importPurchaseRequest(recordKey)
      .unwrap()
      .then(() => {
        successNotification('Import completed successfully!');
        fetchRequest();
      })
      .catch(() => {
        errorNotification('Import not allowed!');
      });
  };

  const handleExport = async (recordKey: string): Promise<void> => {
    const requestIds = recordKey ? [recordKey] : [];

    try {
      const file = await exportPurchaseRequest({ requestIds }).unwrap();
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'purchase-request.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export purchase request:', error);
    }
  };

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  useEffect(() => {
    dispatch(
      manageRequestActions.setActionComponent(
        <PaymentButtonGroup
          onAdd={handleCreateTender}
          onExport={handleExport}
          onImport={handleImport}
        />
      )
    );
  }, []);

  const handleSort = useCallback(
    (sortConfig: SortConfig | null) => {
      fetchRequest(TABLE_PAGE_DEFAULT, sortConfig);

      onChangeCurrentPage(TABLE_PAGE_DEFAULT);
      setSorting(sortConfig);
    },
    [fetchRequest]
  );

  const handleDeleteRequest = useCallback(
    (requestNo: string): void => {
      const reqId = String(requestNo);

      deletePurchaseRequest(reqId)
        .unwrap()
        .then(() => {
          deleteSuccessNotification(reqId);

          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [
      deletePurchaseRequest,
      deleteSuccessNotification,
      errorNotification,
      fetchRequest,
    ]
  );

  const findClassification = useCallback(
    (classificationId: number): string => {
      return (
        purchaseClassifications.find(
          (classification) => classification.codeNum === classificationId
        )?.codeDesc || ''
      );
    },
    [purchaseClassifications]
  );

  const dataSource = useMemo(
    () =>
      dataSourceMap(
        listRequests as RequestListSearchResponse<PurchaseFormResponse>,
        findStatus,
        findClassification
      ),
    [listRequests, findStatus, findClassification]
  );

  const handleChangePage = useCallback((page: number): void => {
    onChangeCurrentPage(page);
    dispatch(eventMessageActions.sendEventType('REFETCH'));
  }, []);

  useEffect(() => {
    dispatch(filterOptionsActions.setFilterOptions(null));
  }, [dispatch]);

  const handleViewRecord = useCallback(
    (recordKey: string): void => {
      dispatch(
        modalActions.setStatusModal({
          status: true,
          key: String(recordKey),
        })
      );
    },
    [dispatch]
  );

  const handleCompare = useCallback(
    (recordKey: string): void => {
      navigate(`comparison-sheet/${recordKey}`);
    },
    [navigate]
  );

  const handleViewWidget = (): void => {
    dispatch(modalActions.setStatusModal({ status: false, key: '' }));
  };

  const handleSearch = useCallback((value: string | null) => {
    setSearchValue(value || undefined);
    dispatch(eventMessageActions.sendEventType('SEARCH'));
  }, []);

  const tagsFormik = {
    ...formik,
    values: tagsValues,
  };

  useEffect(() => {
    if (
      !permissions.can('Search', 'PurchaseRequest') &&
      permissions.can('Search', 'TenderRequest')
    ) {
      navigate('/mr/procurement/tender-request');
    }
  }, [permissions, navigate]);

  return (
    <PresentationContainer>
      {isRequestFormPage ? (
        <Outlet />
      ) : (
        <>
          {permissions.can('Search', 'PurchaseRequest') &&
            permissions.can('Search', 'TenderRequest') && (
              <FiltersWidget
                onSearchSubmit={handleSearch}
                isOpen={isFiltersOpen}
                onClose={closeFilters}
                onOpen={openFilters}
                formik={tagsFormik}
                showTags={showTags}
              >
                <Filters showError={showFiltersError} formik={formik} />
              </FiltersWidget>
            )}
          {isContentLoaded ? (
            <PurchaseRequestList
              onLoadMore={handleLoadMore}
              onView={handleViewRecord}
              onImport={handleImport}
              onExport={handleExport}
              onDelete={handleDeleteRequest}
              onChangePage={handleChangePage}
              onCompare={handleCompare}
              currentPage={currentPage}
              onSort={handleSort}
              listRequests={
                listRequests as RequestListSearchResponse<PurchaseFormResponse>
              }
              requestsDataList={requestsDataList as Array<PurchaseFormResponse>}
              mapColumns={
                permissions.can('Search', 'PurchaseRequest')
                  ? mapColumns
                  : undefined
              }
              dataSource={dataSource}
              requestType="Purchase"
              isFiltersApplied={Boolean(filters)}
              isSearchApplied={Boolean(searchValue)}
            />
          ) : (
            <MSkeleton />
          )}
          <ViewWidget isOpen={isModalOpen} onClose={handleViewWidget}>
            <ViewResolver
              desktop={<RequestViewForm />}
              mobile={
                <RequestViewMobileForm
                  onCancel={() => null}
                  onDelete={handleDeleteRequest}
                  onEdit={() => null}
                  onSubmit={() => null}
                />
              }
            />
          </ViewWidget>
        </>
      )}
    </PresentationContainer>
  );
};

export default PurchaseRequest;
